import React, { useState, useEffect, useRef } from 'react';
import Footer from './Footer';
import './index.css';

function App() {
  // Background image is now loaded from the public folder.
  const appStyle = {
    backgroundImage: `url(${process.env.PUBLIC_URL}/bg.svg)`,
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    minHeight: '100vh',
  };

  const [token, setToken] = useState(null);

  useEffect(() => {
    // Fetch composite_metadata.json from the public folder.
    fetch(`${process.env.PUBLIC_URL}/composite_metadata.json`)
      .then((res) => res.json())
      .then((data) => {
        if (!data.tokens || data.tokens.length === 0) {
          console.error("No tokens found in metadata.");
          return;
        }
        const tokenData = data.tokens.find((t) => t.tokenId === 28) || data.tokens[0];
        setToken(tokenData);
      })
      .catch((err) => console.error("Error loading metadata:", err));
  }, []);

  return (
    <div className="min-h-screen bg-black text-[#ea3323] flex flex-col items-center font-vt323" style={appStyle}>
      {token ? <CompositeViewer token={token} /> : <div className="text-2xl mt-10">Loading metadata...</div>}
    </div>
  );
}

function CompositeViewer({ token }) {
  const layers = token.layers || [];
  const compositeRef = useRef(null);

  // Initialize selections for each layer using its layer key.
  const [selections, setSelections] = useState(() => {
    const init = {};
    layers.forEach((layer) => {
      init[layer.layer] = 0;
    });
    return init;
  });
  const [coatVisible, setCoatVisible] = useState(true);
  const [compositeHeight, setCompositeHeight] = useState(80);

  // Adjust composite container height based on window width.
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setCompositeHeight(68); // Mobile: 15% less than 80vh
      } else {
        setCompositeHeight(80);
      }
    };
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  if (layers.length === 0) {
    return <div className="text-2xl mt-10">Error: No layers found in metadata.</div>;
  }

  const stackingOrder = ["base", "money", "shirt", "trousers", "coat", "head", "left-hand", "right-hand", "socks"];
  const sortedLayers = [...layers].sort(
    (a, b) => stackingOrder.indexOf(a.layer) - stackingOrder.indexOf(b.layer)
  );
  const filteredLayers = sortedLayers.filter(layer =>
    layer.layer === 'coat' ? coatVisible : true
  );

  const toggleOption = (layerKey, optionsCount) => {
    setSelections(prev => ({
      ...prev,
      [layerKey]: (prev[layerKey] + 1) % optionsCount,
    }));
  };

  const randomizeLayers = () => {
    setSelections(prev => {
      const newSelections = { ...prev };
      sortedLayers.forEach(layer => {
        newSelections[layer.layer] = Math.floor(Math.random() * layer.options.length);
      });
      return newSelections;
    });
    setCoatVisible(Math.random() < 0.5);
  };

  const downloadComposite = async () => {
    const canvasWidth = 1500;
    const canvasHeight = 2400;
    const canvas = document.createElement('canvas');
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;
    const ctx = canvas.getContext('2d');

    for (const layer of filteredLayers) {
      const option = layer.options[selections[layer.layer]];
      const img = new Image();
      img.crossOrigin = 'anonymous';
      // Use process.env.PUBLIC_URL to correctly reference images under /banksta
      img.src = `${process.env.PUBLIC_URL}/images/${option.image}`;
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });
      ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);
    }

    canvas.toBlob(blob => {
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'composite.png';
      a.click();
      URL.revokeObjectURL(url);
    }, 'image/png');
  };

  const compositeContainerStyle = {
    height: `${compositeHeight}vh`,
    width: `calc(${compositeHeight}vh * 0.625)`,
  };

  return (
    <>
      <div
        ref={compositeRef}
        className="relative overflow-hidden my-4 border-4 border-[#ea3323] flex-shrink-0"
        style={compositeContainerStyle}
      >
        {filteredLayers.map((layer, index) => (
          <img
            key={layer.layer}
            src={`${process.env.PUBLIC_URL}/images/${layer.options[selections[layer.layer]].image}`}
            alt={layer.options[selections[layer.layer]].label}
            className="absolute top-0 left-0 h-full w-auto"
            style={{ zIndex: index }}
          />
        ))}
      </div>

      <div className="w-full flex flex-col items-center gap-4 px-4 bg-black bg-opacity-70">
        <div className="flex flex-wrap justify-center items-center gap-4 w-full">
          {sortedLayers.map((layer) => {
            const label = layer.options[selections[layer.layer]].label;
            const fontSizeClass = label.length > 10 ? "text-lg" : "text-xl";
            return (
              <div key={layer.layer} className="flex flex-col items-center">
                <div className="text-xl text-[#ea3323] capitalize">{layer.layer}</div>
                {layer.layer === 'coat' ? (
                  <button
                    onClick={() => setCoatVisible(prev => !prev)}
                    className="w-28 h-12 bg-black border-2 border-[#285eec] text-[#ea3323] rounded-md p-1 leading-tight transition-colors duration-300 hover:border-[#ea3323] text-xl"
                  >
                    {coatVisible ? 'Hide' : 'Show'}
                  </button>
                ) : (
                  <button
                    onClick={() => toggleOption(layer.layer, layer.options.length)}
                    className={`w-28 h-12 bg-black border-2 border-[#285eec] text-[#ea3323] rounded-md p-1 leading-tight transition-colors duration-300 hover:border-[#ea3323] ${fontSizeClass}`}
                  >
                    {label}
                  </button>
                )}
              </div>
            );
          })}
          <div className="hidden md:flex flex-row justify-center items-center gap-4">
            <div className="flex flex-col items-center">
              <div className="text-xl text-[#ea3323]">&nbsp;</div>
              <button
                onClick={randomizeLayers}
                className="w-28 h-12 bg-black border-2 border-[#285eec] text-[#ea3323] rounded-md p-1 leading-tight transition-colors duration-300 hover:border-[#ea3323] text-xl"
                title="Randomize Layers"
              >
                🎲
              </button>
            </div>
            <div className="flex flex-col items-center">
              <div className="text-xl text-[#ea3323]">&nbsp;</div>
              <button
                onClick={downloadComposite}
                className="w-28 h-12 bg-black border-2 border-[#285eec] text-[#ea3323] rounded-md p-1 leading-tight transition-colors duration-300 hover:border-[#ea3323] text-xl"
                title="Download Composite"
              >
                ⬇️
              </button>
            </div>
          </div>
          <div className="flex md:hidden flex-row justify-center items-center gap-4 w-full">
            <button
              onClick={randomizeLayers}
              className="w-28 h-12 bg-black border-2 border-[#285eec] text-[#ea3323] rounded-md p-1 leading-tight transition-colors duration-300 hover:border-[#ea3323] text-xl"
              title="Randomize Layers"
            >
              🎲
            </button>
            <button
              onClick={downloadComposite}
              className="w-28 h-12 bg-black border-2 border-[#285eec] text-[#ea3323] rounded-md p-1 leading-tight transition-colors duration-300 hover:border-[#ea3323] text-xl"
              title="Download Composite"
            >
              ⬇️
            </button>
          </div>
        </div>

        <div className="w-full flex justify-center mt-8">
          <Footer />
        </div>
      </div>
    </>
  );
}

export default App;
